import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment/min/moment-with-locales';

import applicationRouter from '~/hoc/applicationRouter';
import withLocalization from '~/hoc/withLocalization';
import withRemoveDialog from '~/hoc/withRemoveDialog';
import Button from '../../../components/CustomButton/CustomButton.jsx';
import { shortDateFormat, dateToTimeFormat } from '~/utils/dateFormat';
import GenericList from '../../../components/GenericList/GenericList.js';
import GenericFilter from '../../../components/GenericList/GenericFilter.js';

import deleteIcon from '../../../assets/img/deleting.svg';
import '../../../styles/css/styles.css';
import '../../../styles/scss/visitor.scss';

const VISITOR_STATUS_OPTIONS = [
    { code: 'approved', name: 'Approved' },
    { code: 'pending',  name: 'Pending'  },
    { code: 'rejected', name: 'Rejected' },
];


class VisitorLog extends Component {
    constructor(props) {
        super(props);
        this.state = {
            zones: [],
            filters: { name: '', status: '', zone_id: '' },
            appliedFilters: {},
            approvedCount: 0,
            pendingCount:  0,
            rejectedCount: 0,
        };
    }

    componentDidMount() {
        this.loadZones();
    }

    loadZones = async () => {
        const { visitorStore } = this.props;
        try {
            await visitorStore.loadZones();
            this.setState({ zones: visitorStore.zones });
        } catch (e) {
            console.error('loadZones error', e);
        }
    };

    // ── GenericFilter wiring ──────────────────────────────────────────────────

    setFilter = (name, value) => {
        this.setState(prev => ({ filters: { ...prev.filters, [name]: value } }));
    };

    onFilter = () => {
        this.setState(prev => ({ appliedFilters: { ...prev.filters } }));
    };

    // ── Data ──────────────────────────────────────────────────────────────────

    requestData = (params) => {
        const { filters, ...rest } = params;
        const flatParams = { ...rest, ...(filters || {}) };
        return this.props.visitorStore.getVisitorLogs(flatParams).then(data => {
            this.setState({
                approvedCount: data.approvedCount || 0,
                pendingCount:  data.pendingCount  || 0,
                rejectedCount: data.rejectedCount || 0,
            });
            return data;
        });
    };

    // ── Actions ───────────────────────────────────────────────────────────────

    openDetails = (id) => {
        this.props.router.navigate(`/admin/visitorLog/${id}`);
    };

    handleCheckout = async (id) => {
        const { visitorStore, commonStore, t } = this.props;
        try {
            await visitorStore.checkoutVisitorLog(id);
            commonStore.addNotification(t('Checked out'), null, 'success');
            visitorStore.resetVisitorLogsLastLoadTime();
        } catch (e) {
            commonStore.addNotification(t('Error'), null, 'error');
        }
    };

    handleDelete = (id) => {
        const { commonStore, t, visitorStore } = this.props;
        return this.props.handleDelete(id, async (id) => {
            await visitorStore.deleteVisitorLog(id);
            commonStore.addNotification(t('Visitor Removed'), null, 'error');
            visitorStore.resetVisitorLogsLastLoadTime();
        });
    };

    // ── Helpers ───────────────────────────────────────────────────────────────

    getStatusBadge = (status) => {
        const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending';
        return (
            <div className={`vl-status-block vl-status-${status || 'pending'}`}>
                {label}
            </div>
        );
    };

    getColumns = () => {
        const { t } = this.props;
        const dateTimeRules = this.props.commonStore.getDateTimeRules();
        return [
            {
                Header: t('Visitor Name'),
                accessor: 'visitor_name',
                isSortable: true,
                Cell: ({ row }) => <span className="vl-cell-text font-weight-bold">{row.original.visitor_name}</span>,
            },
            {
                Header: t('Company'),
                accessor: 'visitor_company',
                isSortable: true,
                Cell: ({ row }) => <span className="vl-cell-text">{row.original.visitor_company || '-'}</span>,
            },
            {
                Header: t('Email'),
                accessor: 'visitor_email',
                isSortable: false,
                Cell: ({ row }) => <span className="vl-cell-text">{row.original.visitor_email || '-'}</span>,
            },
            {
                Header: t('Mobile No'),
                accessor: 'visitor_phone',
                isSortable: false,
                Cell: ({ row }) => <span className="vl-cell-text">{row.original.visitor_phone || '-'}</span>,
            },
            {
                Header: t('Zone'),
                accessor: 'zone_name',
                isSortable: false,
                Cell: ({ row }) => <span className="vl-cell-text">{row.original.zone_name || '-'}</span>,
            },
            {
                Header: t('Check In'),
                accessor: 'check_in_time',
                isSortable: true,
                Cell: ({ row }) => {
                    return row.original.check_in_time
                        ? `${shortDateFormat(row.original.check_in_time, dateTimeRules)} ${dateToTimeFormat(row.original.check_in_time, dateTimeRules)}`
                        : '-';
                },
            },
            {
                Header: t('Check Out'),
                accessor: 'check_out_time',
                isSortable: false,
                Cell: ({ row }) => {
                    return row.original.check_out_time
                        ? `${shortDateFormat(row.original.check_out_time, dateTimeRules)} ${dateToTimeFormat(row.original.check_out_time, dateTimeRules)}`
                        : '-';
                },
            },
            {
                Header: t('Contact Person'),
                accessor: 'contact_person_name',
                isSortable: false,
                Cell: ({ row }) => row.original.contact_person_name || '-',
            },
            {
                Header: t('Status'),
                accessor: 'status',
                isSortable: true,
                Cell: ({ row }) => {
                    const d = row.original.data || {};
                    const pmPending = d.requires_pm_review && d.pm_review_status === 'pending';
                    return (
                        <div className="d-flex flex-column align-items-center vl-status-col">
                            {this.getStatusBadge(row.original.status)}
                            {pmPending && (
                                <span className="badge vl-pm-badge">
                                    <i className="fa fa-exclamation-triangle mr-1" />
                                    {t('PM Review')}
                                </span>
                            )}
                        </div>
                    );
                },
            },
            {
                Header: t('Action'),
                id: 'actions',
                accessor: row => row.id,
                isSortable: false,
                Cell: ({ row }) => (
                    <div className="actions-center justify-content-center">
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tip-view">{t('View Details')}</Tooltip>}>
                            <Button icon_sm fill onClick={() => this.openDetails(row.original.id)}>
                                <i className="fa fa-eye" />
                            </Button>
                        </OverlayTrigger>
                        {(() => {
                            const canCheckout = row.original.status === 'approved' && !row.original.check_out_time;
                            return (
                                <OverlayTrigger placement="top" overlay={<Tooltip id="tip-checkout">{canCheckout ? t('Check Out') : row.original.check_out_time ? t('Already checked out') : t('Not available for checkout')}</Tooltip>}>
                                    <span>
                                        <Button icon_sm fill disabled={!canCheckout} onClick={canCheckout ? () => this.handleCheckout(row.original.id) : undefined}>
                                            <i className="fas fa-sign-out-alt" />
                                        </Button>
                                    </span>
                                </OverlayTrigger>
                            );
                        })()}
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tip-delete">{t('Delete')}</Tooltip>}>
                            <Button icon_sm_delete fill onClick={() => this.handleDelete(row.original.id)}>
                                <img src={deleteIcon} alt="delete" />
                            </Button>
                        </OverlayTrigger>
                    </div>
                ),
            },
        ];
    };

    render() {
        const { t, visitorStore } = this.props;
        const { filters, appliedFilters, zones } = this.state;
        const zoneOptions = [
            { value: '', label: t('All Zones') },
            ...zones.map(z => ({ value: z.id, label: z.name })),
        ];

        return (
            <>
                <div className="d-flex align-items-center mb-3 px-3 py-2 border rounded text-danger bg-white">
                    <i className="fa fa-clock-o mr-2" />
                    <span>{t('Data Prior To 14 Days Will Be Deleted By The System')}</span>
                </div>
                <GenericList
                    columns={this.getColumns()}
                    requestData={this.requestData}
                    filters={appliedFilters}
                    lastListLoadTime={visitorStore.visitorLogsLastLoadTime}
                    tableHeader={t('Visitor Log')}
                    splitHeader
                    header={
                        <GenericFilter
                            filters={filters}
                            setFilter={this.setFilter}
                            onFilter={this.onFilter}
                            hasSearch
                            searchFilterPlaceholder="Search visitor name..."
                            hasStatusFilter
                            statusOptions={VISITOR_STATUS_OPTIONS}
                            hasCustomFilter
                            customFilterName={{ value: 'zone_id' }}
                            customFilterOptions={zoneOptions}
                        />
                    }
                />
            </>
        );
    }
}

export default inject('visitorStore', 'userStore', 'commonStore')(
    applicationRouter(withLocalization(withRemoveDialog(observer(VisitorLog))))
);
